'use client'

import { useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import {
  faInbox,
  faTrashAlt,
  faCheck,
  faEllipsis,
} from '@document-crunch/pro-regular-svg-icons'

import { Button } from '@dc-shared-web/components/shadcn/button'
import { Switch } from '@dc-shared-web/components/shadcn/switch'
import { Tooltip } from '@/dc-shared-web/src/components/base/Tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dc-shared-web/components/shadcn/popover'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@dc-shared-web/components/shadcn/dropdown-menu'
import { Icon, IconColor } from '@/dc-shared-web/src/components/base/Icon'

import {
  useDeleteAllInboxNotifications,
  useMarkAllInboxNotificationsAsRead,
  useUserNotifications,
} from '@/app/core/providers/UserNotificationProvider'

import NotificationInbox from '@/app/core/components/notification/NotificationInbox'
import NotificationLoading from '@/app/core/components/notification/NotificationLoading'
import { isNotificationUnread } from '@/app/core/components/notification/utils'

function InboxPopoverUnreadCount() {
  const { notifications } = useUserNotifications()

  const hasUnreadNotifications = useMemo(
    () =>
      notifications.some((notification) => isNotificationUnread(notification)),
    [notifications]
  )

  return hasUnreadNotifications ? (
    <span
      className='tw-absolute tw-top-1.5 tw-right-1.5 tw-w-3.5 tw-h-3.5 tw-bg-red-500 tw-rounded-full tw-border-w tw-border-2 tw-border-white'
      data-testid='notifications-unread-icon'
    />
  ) : null
}

export default function NotificationPopoverPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [onlyShowUnread, setOnlyShowUnread] = useState(false)

  const { isLoading } = useUserNotifications()

  const markAllInboxNotificationsAsRead = useMarkAllInboxNotificationsAsRead()
  const deleteAllInboxNotifications = useDeleteAllInboxNotifications()

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content='Notifications' sideOffset={-2} withArrow>
        <PopoverTrigger className={'tw-sticky'} asChild>
          <Button
            size='icon'
            variant='ghost'
            data-testid='notifications-button'
          >
            <Icon icon={faInbox} color={IconColor.darkPrimary} />
            <ErrorBoundary fallback={null}>
              <InboxPopoverUnreadCount />
            </ErrorBoundary>
          </Button>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align='end'
        className='tw-w-[458px] tw-z-10 tw-p-0 tw-rounded-xl tw-max-h-[calc(100vh_-_64px)] tw-flex tw-flex-col'
        collisionPadding={16}
        sideOffset={8}
      >
        <div className='tw-flex tw-items-center tw-justify-between tw-p-4 tw-basis-10'>
          <span className='tw-font-medium tw-text-lg'>Notifications</span>
          <div className='tw-flex tw-items-center tw-gap-4'>
            <div className='tw-flex tw-items-center'>
              <Switch
                checked={onlyShowUnread}
                onCheckedChange={setOnlyShowUnread}
                data-testid='notifications-unread-switch'
              />
              <span className='tw-ml-2 tw-text-sm'>Only show unread</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size='icon'
                  variant='ghost'
                  data-testid='notifications-action-button'
                >
                  <Icon icon={faEllipsis} color={IconColor.primary} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className='tw-rounded-lg tw-bg-white tw-shadow-[0_2px_4px_#00000014,0_4px_12px_#0000001f] tw-p-1 tw-gap-0'
                align='end'
              >
                <DropdownMenuItem
                  className='tw-cursor-pointer'
                  onClick={markAllInboxNotificationsAsRead}
                  data-testid='notifications-mark-all-read-button'
                >
                  <Icon
                    icon={faCheck}
                    color={IconColor.darkPrimary}
                    className='tw-mr-3'
                  />
                  <span className='tw-text-sm tw-text-neutral-700'>
                    Mark all as read
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className='tw-cursor-pointer'
                  onClick={deleteAllInboxNotifications}
                  data-testid='notifications-delete-all-button'
                >
                  <Icon
                    icon={faTrashAlt}
                    color={IconColor.darkPrimary}
                    className='tw-mr-3'
                  />
                  <span className='tw-text-sm tw-text-neutral-700'>
                    Delete all notifications
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <DropdownMenuSeparator className='tw-m-0' />
        <ErrorBoundary
          fallback={
            <div className='tw-p-4'>
              There was an error while getting notifications.
            </div>
          }
        >
          {isLoading ? (
            <NotificationLoading />
          ) : (
            <NotificationInbox
              onlyShowUnread={onlyShowUnread}
              onNavigateToComment={() => setIsOpen(false)}
            />
          )}
        </ErrorBoundary>
      </PopoverContent>
    </Popover>
  )
}
